import { DEFAULT_TIMEZONE, LANGUAGE, LANGUAGE_TYPE } from './constants';

import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import timezone from 'dayjs/plugin/timezone';
import calendar from 'dayjs/plugin/calendar';
import utc from 'dayjs/plugin/utc';

import i18n from '../i18n';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);
dayjs.extend(duration);
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(calendar);

const getDateInTimezone = (
  date: string,
  timezoneName: string,
  locale?: string
) => {
  const localizedDate = locale ? dayjs(date).locale(locale) : dayjs(date);

  if (typeof localizedDate.tz === 'function') {
    return localizedDate.tz(timezoneName);
  }

  if (typeof dayjs.tz === 'function') {
    const zonedDate = dayjs.tz(date, timezoneName);
    return locale ? zonedDate.locale(locale) : zonedDate;
  }

  return localizedDate;
};

const getUtcDateInTimezone = (date: string, timezoneName: string) => {
  const utcDate =
    typeof dayjs.utc === 'function' ? dayjs.utc(date) : dayjs(date);

  if (typeof utcDate.tz === 'function') {
    return utcDate.tz(timezoneName);
  }

  if (typeof dayjs.tz === 'function') {
    return dayjs.tz(date, timezoneName);
  }

  return utcDate;
};

export const eventTimeFormat = (
  startDate: string,
  endDate: string,
  language: (typeof LANGUAGE_TYPE)[number],
  timezone: string = DEFAULT_TIMEZONE
) => {
  const startDateWithLocale = getDateInTimezone(startDate, timezone, language);
  const endDateWithLocale = getDateInTimezone(endDate, timezone, language);

  const startYear = startDateWithLocale.format('YYYY');
  const endYear = endDateWithLocale.format('YYYY');
  const startMonth =
    language === LANGUAGE.ENGLISH
      ? startDateWithLocale.format('MMMM')
      : startDateWithLocale.format('MMMM').split('.').join('');
  const endMonth =
    language === LANGUAGE.ENGLISH
      ? endDateWithLocale.format('MMMM')
      : endDateWithLocale.format('MMMM').split('.').join('');
  const startDay = startDateWithLocale.format('DD');
  const endDay = endDateWithLocale.format('DD');

  if (language === LANGUAGE.ENGLISH) {
    if (startYear !== endYear)
      return `${startMonth} ${startDay}, ${startYear} - ${endMonth} ${endDay}, ${endYear}`;
    else if (startMonth === endMonth) {
      if (startDay === endDay) return `${endMonth} ${endDay}, ${endYear}`;
      else return `${endMonth} ${startDay} - ${endDay}, ${endYear}`;
    } else
      return `${startMonth} ${startDay} - ${endMonth} ${endDay}, ${endYear}`;
  } else {
    if (startYear !== endYear)
      return `${startMonth} ${startDay},  ${startYear} - ${endMonth} ${endDay},  ${endYear}`;
    else if (startMonth === endMonth) {
      if (startDay === endDay) return `${startDay}. ${endMonth}  ${endYear}`;
      else return `${startDay}. - ${endDay}. ${endMonth} ${endYear}`;
    } else
      return `${startMonth} ${startDay} - ${endMonth} ${endDay},  ${endYear}`;
  }
};

export const isFutureDate = (date: string) => {
  return dayjs(date).isAfter(dayjs());
};
export const findClosestDate = (dates: string[]) => {
  const now = new Date();

  const closestDate = dates.reduce((a, b) => {
    const aDate = new Date(a);
    const bDate = new Date(b);
    return Math.abs(aDate.getTime() - now.getTime()) <
      Math.abs(bDate.getTime() - now.getTime())
      ? a
      : b;
  }, '');

  return closestDate;
};

export const formatDuration = (duration: number) => {
  if (duration) {
    const hours = Math.floor(duration / 3600);
    if (hours > 0)
      return new Date(duration * 1000).toISOString().substring(11, 19);
    return new Date(duration * 1000).toISOString().substring(14, 19);
  }
  return '00:00';
};

export const getUniqueDays = (dates: string[]) => {
  return [
    ...new Set(
      dates
        ?.map(e => dayjs(e).unix())
        .sort((a, b) => a - b)
        ?.map(e => dayjs.unix(e).format('MM-DD-YYYY'))
    ),
  ];
};

export const getUniqueDaysTimezoneIndependent = (dates: string[]) => {
  return [
    ...new Set(dates.map(d => dayjs(d.split('T')[0]).format('MM-DD-YYYY'))),
  ];
};

export const getUniquePdfDays = (
  dates: string[],
  locale: 'en' | 'de' = 'en'
) => {
  const format = locale === 'de' ? 'dddd, D. MMMM YYYY' : 'dddd, D MMMM YYYY';

  return [
    ...new Set(
      dates
        ?.map(date => dayjs(date).locale(locale).format(format))
        .sort((a, b) => dayjs(a, format).unix() - dayjs(b, format).unix())
    ),
  ];
};

export const counterDate = (date: string) => {
  if (!date) return null;

  const now = dayjs();
  const target = dayjs(date);

  if (target.isBefore(now)) return null;

  const diff = target.diff(now);
  const duration = dayjs.duration(diff);

  const totalDays = Math.round(duration.asDays());
  if (totalDays > 0) {
    const quantity = totalDays === 1 ? i18n.t('day') : i18n.t('days-v2');
    return `${totalDays} ${quantity}`;
  }

  const totalHours = Math.round(duration.asHours());
  if (totalHours > 0) {
    const quantity = totalHours === 1 ? i18n.t('hour') : i18n.t('hours');
    return `${totalHours} ${quantity}`;
  }

  const totalMinutes = Math.round(duration.asMinutes());
  if (totalMinutes > 0) {
    const quantity = totalMinutes === 1 ? i18n.t('minute') : i18n.t('minutes');
    return `${totalMinutes} ${quantity}`;
  }

  return null;
};

export const formatDate = (date: string) => {
  const dateItems = date?.split('-');
  return `${dateItems[2]}-${dateItems[0]}-${dateItems[1]}`;
};

export const formatDateToICS = (date: string) => {
  return dayjs(date).format('YYYYMMDDTHHMMss') + 'Z';
};

export function formatLocalTimeZone(utcDateStr: string) {
  // Automatically detect system timezone
  const localTz = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const dt = getUtcDateInTimezone(utcDateStr, localTz);

  // Compute GMT offset
  const offsetHours = Math.floor(dt.utcOffset() / 60);
  const offsetMinutes = Math.abs(dt.utcOffset() % 60);
  const sign = offsetHours >= 0 ? '+' : '-';
  const gmtOffset = `GMT${sign}${String(Math.abs(offsetHours)).padStart(2, '0')}:${String(offsetMinutes).padStart(2, '0')}`;

  return `(${gmtOffset}) ${localTz.replace('_', ' ')}`;
}

export const getSessionTime = (
  date1: string,
  date2: string,
  timezone?: string
) => {
  const tz = timezone ?? DEFAULT_TIMEZONE;

  const formatDayjsAsDateTimeString = (date: dayjs.Dayjs) => {
    if (!date || !dayjs(date)?.isValid()) return '';
    return date?.format('HH:mm');
  };

  const startDate = getDateInTimezone(date1, tz);
  const endDate = getDateInTimezone(date2, tz);

  const time = `${formatDayjsAsDateTimeString(startDate)} - ${formatDayjsAsDateTimeString(
    endDate
  )}`;

  return time;
};

export const getSessionTimeWithoutTimezone = (date1: string, date2: string) => {
  const format = (iso: string) => {
    if (!iso) return '';

    const match = iso.match(/T(\d{2}):(\d{2})/);
    if (!match) return '';

    return `${match[1]}:${match[2]}`;
  };

  return `${format(date1)} - ${format(date2)}`;
};

export const getSessionTimeWithoutTimezonePDF = (
  date1: string,
  date2: string,
  lang: string
) => {
  const format = (iso: string) => {
    if (!iso) return '';

    const match = iso.match(/T(\d{2}):(\d{2})/);
    if (!match) return '';

    let hours = parseInt(match[1], 10);
    const minutes = match[2];

    // German → keep 24h format
    if (lang === 'de') {
      return `${match[1]}:${minutes}`;
    }

    // English → convert to 12h
    const period = hours >= 12 ? 'pm' : 'am';
    hours = hours % 12 || 12;

    return `${hours}:${minutes} ${period}`;
  };

  return `${format(date1)} - ${format(date2)} ${lang === 'de' ? 'Uhr' : ''}`;
};

export const timezoneToCountry: Record<string, string> = {
  'Africa/Abidjan': 'ci',
  'Africa/Accra': 'gh',
  'Africa/Addis_Ababa': 'et',
  'Africa/Algiers': 'dz',
  'Africa/Asmara': 'er',
  'Africa/Bamako': 'ml',
  'Africa/Bangui': 'cf',
  'Africa/Banjul': 'gm',
  'Africa/Bissau': 'gw',
  'Africa/Blantyre': 'mw',
  'Africa/Brazzaville': 'cg',
  'Africa/Bujumbura': 'bi',
  'Africa/Cairo': 'eg',
  'Africa/Casablanca': 'ma',
  'Africa/Ceuta': 'es',
  'Africa/Conakry': 'gn',
  'Africa/Dakar': 'sn',
  'Africa/Dar_es_Salaam': 'tz',
  'Africa/Djibouti': 'dj',
  'Africa/Douala': 'cm',
  'Africa/El_Aaiun': 'eh',
  'Africa/Freetown': 'sl',
  'Africa/Gaborone': 'bw',
  'Africa/Harare': 'zw',
  'Africa/Johannesburg': 'za',
  'Africa/Juba': 'ss',
  'Africa/Kampala': 'ug',
  'Africa/Khartoum': 'sd',
  'Africa/Kigali': 'rw',
  'Africa/Kinshasa': 'cd',
  'Africa/Lagos': 'ng',
  'Africa/Libreville': 'ga',
  'Africa/Lome': 'tg',
  'Africa/Luanda': 'ao',
  'Africa/Lubumbashi': 'cd',
  'Africa/Lusaka': 'zm',
  'Africa/Malabo': 'gq',
  'Africa/Maputo': 'mz',
  'Africa/Maseru': 'ls',
  'Africa/Mbabane': 'sz',
  'Africa/Mogadishu': 'so',
  'Africa/Monrovia': 'lr',
  'Africa/Nairobi': 'ke',
  'Africa/Ndjamena': 'td',
  'Africa/Niamey': 'ne',
  'Africa/Nouakchott': 'mr',
  'Africa/Ouagadougou': 'bf',
  'Africa/Porto-Novo': 'bj',
  'Africa/Sao_Tome': 'st',
  'Africa/Tripoli': 'ly',
  'Africa/Tunis': 'tn',
  'Africa/Windhoek': 'na',

  'America/Adak': 'us',
  'America/Anchorage': 'us',
  'America/Anguilla': 'ai',
  'America/Antigua': 'ag',
  'America/Araguaina': 'br',
  'America/Argentina/Buenos_Aires': 'ar',
  'America/Argentina/Catamarca': 'ar',
  'America/Argentina/Cordoba': 'ar',
  'America/Argentina/Jujuy': 'ar',
  'America/Argentina/La_Rioja': 'ar',
  'America/Argentina/Mendoza': 'ar',
  'America/Argentina/Rio_Gallegos': 'ar',
  'America/Argentina/Salta': 'ar',
  'America/Argentina/San_Juan': 'ar',
  'America/Argentina/San_Luis': 'ar',
  'America/Argentina/Tucuman': 'ar',
  'America/Argentina/Ushuaia': 'ar',
  'America/Aruba': 'aw',
  'America/Asuncion': 'py',
  'America/Atikokan': 'ca',
  'America/Bahia': 'br',
  'America/Bahia_Banderas': 'mx',
  'America/Barbados': 'bb',
  'America/Belem': 'br',
  'America/Belize': 'bz',
  'America/Blanc-Sablon': 'ca',
  'America/Boa_Vista': 'br',
  'America/Bogota': 'co',
  'America/Boise': 'us',
  'America/Cambridge_Bay': 'ca',
  'America/Campo_Grande': 'br',
  'America/Cancun': 'mx',
  'America/Caracas': 've',
  'America/Cayenne': 'gf',
  'America/Cayman': 'ky',
  'America/Chicago': 'us',
  'America/Chihuahua': 'mx',
  'America/Costa_Rica': 'cr',
  'America/Creston': 'ca',
  'America/Cuiaba': 'br',
  'America/Curacao': 'cw',
  'America/Danmarkshavn': 'gl',
  'America/Dawson': 'ca',
  'America/Dawson_Creek': 'ca',
  'America/Denver': 'us',
  'America/Detroit': 'us',
  'America/Dominica': 'dm',
  'America/Edmonton': 'ca',
  'America/Eirunepe': 'br',
  'America/El_Salvador': 'sv',
  'America/Fort_Nelson': 'ca',
  'America/Fortaleza': 'br',
  'America/Glace_Bay': 'ca',
  'America/Godthab': 'gl',
  'America/Goose_Bay': 'ca',
  'America/Grand_Turk': 'tc',
  'America/Grenada': 'gd',
  'America/Guadeloupe': 'gp',
  'America/Guatemala': 'gt',
  'America/Guayaquil': 'ec',
  'America/Guyana': 'gy',
  'America/Halifax': 'ca',
  'America/Havana': 'cu',
  'America/Hermosillo': 'mx',
  'America/Indiana/Indianapolis': 'us',
  'America/Indiana/Knox': 'us',
  'America/Indiana/Marengo': 'us',
  'America/Indiana/Petersburg': 'us',
  'America/Indiana/Tell_City': 'us',
  'America/Indiana/Vevay': 'us',
  'America/Indiana/Vincennes': 'us',
  'America/Indiana/Winamac': 'us',
  'America/Inuvik': 'ca',
  'America/Iqaluit': 'ca',
  'America/Jamaica': 'jm',
  'America/Juneau': 'us',
  'America/Kentucky/Louisville': 'us',
  'America/Kentucky/Monticello': 'us',
  'America/La_Paz': 'bo',
  'America/Lima': 'pe',
  'America/Los_Angeles': 'us',
  'America/Lower_Princes': 'sx',
  'America/Maceio': 'br',
  'America/Managua': 'ni',
  'America/Manaus': 'br',
  'America/Marigot': 'mf',
  'America/Martinique': 'mq',
  'America/Matamoros': 'mx',
  'America/Mazatlan': 'mx',
  'America/Menominee': 'us',
  'America/Merida': 'mx',
  'America/Metlakatla': 'us',
  'America/Mexico_City': 'mx',
  'America/Miquelon': 'pm',
  'America/Moncton': 'ca',
  'America/Monterrey': 'mx',
  'America/Montevideo': 'uy',
  'America/Montserrat': 'ms',
  'America/Nassau': 'bs',
  'America/New_York': 'us',
  'America/Nipigon': 'ca',
  'America/Nome': 'us',
  'America/Noronha': 'br',
  'America/North_Dakota/Beulah': 'us',
  'America/North_Dakota/Center': 'us',
  'America/North_Dakota/New_Salem': 'us',
  'America/Ojinaga': 'mx',
  'America/Panama': 'pa',
  'America/Pangnirtung': 'ca',
  'America/Paramaribo': 'sr',
  'America/Phoenix': 'us',
  'America/Port-au-Prince': 'ht',
  'America/Port_of_Spain': 'tt',
  'America/Porto_Velho': 'br',
  'America/Puerto_Rico': 'pr',
  'America/Rainy_River': 'ca',
  'America/Rankin_Inlet': 'ca',
  'America/Recife': 'br',
  'America/Regina': 'ca',
  'America/Resolute': 'ca',
  'America/Rio_Branco': 'br',
  'America/Santarem': 'br',
  'America/Santiago': 'cl',
  'America/Santo_Domingo': 'do',
  'America/Sao_Paulo': 'br',
  'America/Scoresbysund': 'gl',
  'America/Sitka': 'us',
  'America/St_Barthelemy': 'bl',
  'America/St_Johns': 'ca',
  'America/St_Kitts': 'kn',
  'America/St_Lucia': 'lc',
  'America/St_Thomas': 'vi',
  'America/St_Vincent': 'vc',
  'America/Swift_Current': 'ca',
  'America/Tegucigalpa': 'hn',
  'America/Thule': 'gl',
  'America/Thunder_Bay': 'ca',
  'America/Tijuana': 'mx',
  'America/Toronto': 'ca',
  'America/Tortola': 'vg',
  'America/Vancouver': 'ca',
  'America/Whitehorse': 'ca',
  'America/Winnipeg': 'ca',
  'America/Yakutat': 'us',
  'America/Yellowknife': 'ca',

  'Antarctica/Casey': 'aq',
  'Antarctica/Davis': 'aq',
  'Antarctica/DumontDUrville': 'aq',
  'Antarctica/Macquarie': 'au',
  'Antarctica/Mawson': 'aq',
  'Antarctica/Palmer': 'aq',
  'Antarctica/Rothera': 'aq',
  'Antarctica/Syowa': 'aq',
  'Antarctica/Troll': 'no',
  'Antarctica/Vostok': 'aq',

  'Asia/Aden': 'ye',
  'Asia/Almaty': 'kz',
  'Asia/Amman': 'jo',
  'Asia/Anadyr': 'ru',
  'Asia/Aqtau': 'kz',
  'Asia/Aqtobe': 'kz',
  'Asia/Ashgabat': 'tm',
  'Asia/Baghdad': 'iq',
  'Asia/Bahrain': 'bh',
  'Asia/Baku': 'az',
  'Asia/Bangkok': 'th',
  'Asia/Barnaul': 'ru',
  'Asia/Beirut': 'lb',
  'Asia/Bishkek': 'kg',
  'Asia/Brunei': 'bn',
  'Asia/Chita': 'ru',
  'Asia/Choibalsan': 'mn',
  'Asia/Colombo': 'lk',
  'Asia/Damascus': 'sy',
  'Asia/Dhaka': 'bd',
  'Asia/Dili': 'tl',
  'Asia/Dubai': 'ae',
  'Asia/Dushanbe': 'tj',
  'Asia/Famagusta': 'cy',
  'Asia/Gaza': 'ps',
  'Asia/Hebron': 'ps',
  'Asia/Ho_Chi_Minh': 'vn',
  'Asia/Hong_Kong': 'hk',
  'Asia/Hovd': 'mn',
  'Asia/Irkutsk': 'ru',
  'Asia/Jakarta': 'id',
  'Asia/Jayapura': 'id',
  'Asia/Jerusalem': 'il',
  'Asia/Kabul': 'af',
  'Asia/Kamchatka': 'ru',
  'Asia/Karachi': 'pk',
  'Asia/Kathmandu': 'np',
  'Asia/Khandyga': 'ru',
  'Asia/Kolkata': 'in',
  'Asia/Krasnoyarsk': 'ru',
  'Asia/Kuala_Lumpur': 'my',
  'Asia/Kuching': 'my',
  'Asia/Kuwait': 'kw',
  'Asia/Macau': 'mo',
  'Asia/Magadan': 'ru',
  'Asia/Makassar': 'id',
  'Asia/Manila': 'ph',
  'Asia/Muscat': 'om',
  'Asia/Nicosia': 'cy',
  'Asia/Novokuznetsk': 'ru',
  'Asia/Novosibirsk': 'ru',
  'Asia/Omsk': 'ru',
  'Asia/Oral': 'kz',
  'Asia/Phnom_Penh': 'kh',
  'Asia/Pontianak': 'id',
  'Asia/Pyongyang': 'kp',
  'Asia/Qatar': 'qa',
  'Asia/Qostanay': 'kz',
  'Asia/Qyzylorda': 'kz',
  'Asia/Riyadh': 'sa',
  'Asia/Sakhalin': 'ru',
  'Asia/Samarkand': 'uz',
  'Asia/Seoul': 'kr',
  'Asia/Shanghai': 'cn',
  'Asia/Singapore': 'sg',
  'Asia/Srednekolymsk': 'ru',
  'Asia/Taipei': 'tw',
  'Asia/Tashkent': 'uz',
  'Asia/Tbilisi': 'ge',
  'Asia/Tehran': 'ir',
  'Asia/Thimphu': 'bt',
  'Asia/Tokyo': 'jp',
  'Asia/Tomsk': 'ru',
  'Asia/Ulaanbaatar': 'mn',
  'Asia/Urumqi': 'cn',
  'Asia/Ust-Nera': 'ru',
  'Asia/Vientiane': 'la',
  'Asia/Vladivostok': 'ru',
  'Asia/Yakutsk': 'ru',
  'Asia/Yangon': 'mm',
  'Asia/Yekaterinburg': 'ru',
  'Asia/Yerevan': 'am',

  'Atlantic/Azores': 'pt',
  'Atlantic/Bermuda': 'bm',
  'Atlantic/Canary': 'es',
  'Atlantic/Cape_Verde': 'cv',
  'Atlantic/Faroe': 'fo',
  'Atlantic/Madeira': 'pt',
  'Atlantic/Reykjavik': 'is',
  'Atlantic/South_Georgia': 'gs',
  'Atlantic/St_Helena': 'sh',
  'Atlantic/Stanley': 'fk',

  'Australia/Adelaide': 'au',
  'Australia/Brisbane': 'au',
  'Australia/Broken_Hill': 'au',
  'Australia/Currie': 'au',
  'Australia/Darwin': 'au',
  'Australia/Eucla': 'au',
  'Australia/Hobart': 'au',
  'Australia/Lindeman': 'au',
  'Australia/Lord_Howe': 'au',
  'Australia/Melbourne': 'au',
  'Australia/Perth': 'au',
  'Australia/Sydney': 'au',

  'Europe/Amsterdam': 'nl',
  'Europe/Andorra': 'ad',
  'Europe/Astrakhan': 'ru',
  'Europe/Athens': 'gr',
  'Europe/Belgrade': 'rs',
  'Europe/Berlin': 'de',
  'Europe/Brussels': 'be',
  'Europe/Bucharest': 'ro',
  'Europe/Budapest': 'hu',
  'Europe/Chisinau': 'md',
  'Europe/Copenhagen': 'dk',
  'Europe/Dublin': 'ie',
  'Europe/Gibraltar': 'gi',
  'Europe/Helsinki': 'fi',
  'Europe/Istanbul': 'tr',
  'Europe/Kaliningrad': 'ru',
  'Europe/Kiev': 'ua',
  'Europe/Kirov': 'ru',
  'Europe/Lisbon': 'pt',
  'Europe/Ljubljana': 'si',
  'Europe/London': 'gb',
  'Europe/Luxembourg': 'lu',
  'Europe/Madrid': 'es',
  'Europe/Malta': 'mt',
  'Europe/Mariehamn': 'ax',
  'Europe/Minsk': 'by',
  'Europe/Monaco': 'mc',
  'Europe/Moscow': 'ru',
  'Europe/Oslo': 'no',
  'Europe/Paris': 'fr',
  'Europe/Podgorica': 'me',
  'Europe/Prague': 'cz',
  'Europe/Riga': 'lv',
  'Europe/Rome': 'it',
  'Europe/Samara': 'ru',
  'Europe/San_Marino': 'sm',
  'Europe/Sarajevo': 'ba',
  'Europe/Saratov': 'ru',
  'Europe/Simferopol': 'ua',
  'Europe/Skopje': 'mk',
  'Europe/Sofia': 'bg',
  'Europe/Stockholm': 'se',
  'Europe/Tallinn': 'ee',
  'Europe/Tirane': 'al',
  'Europe/Ulyanovsk': 'ru',
  'Europe/Uzhgorod': 'ua',
  'Europe/Vaduz': 'li',
  'Europe/Vatican': 'va',
  'Europe/Vienna': 'at',
  'Europe/Vilnius': 'lt',
  'Europe/Volgograd': 'ru',
  'Europe/Warsaw': 'pl',
  'Europe/Zagreb': 'hr',
  'Europe/Zaporozhye': 'ua',
  'Europe/Zurich': 'ch',

  'Indian/Chagos': 'io',
  'Indian/Christmas': 'cx',
  'Indian/Cocos': 'cc',
  'Indian/Kerguelen': 'tf',
  'Indian/Mahe': 'sc',
  'Indian/Maldives': 'mv',
  'Indian/Mauritius': 'mu',
  'Indian/Mayotte': 'yt',
  'Indian/Reunion': 're',

  'Pacific/Apia': 'ws',
  'Pacific/Auckland': 'nz',
  'Pacific/Bougainville': 'pg',
  'Pacific/Chatham': 'nz',
  'Pacific/Chuuk': 'fm',
  'Pacific/Easter': 'cl',
  'Pacific/Efate': 'vu',
  'Pacific/Enderbury': 'ki',
  'Pacific/Fakaofo': 'tk',
  'Pacific/Fiji': 'fj',
  'Pacific/Funafuti': 'tv',
  'Pacific/Galapagos': 'ec',
  'Pacific/Gambier': 'pf',
  'Pacific/Guadalcanal': 'sb',
  'Pacific/Guam': 'gu',
  'Pacific/Honolulu': 'us',
  'Pacific/Kiritimati': 'ki',
  'Pacific/Kosrae': 'fm',
  'Pacific/Kwajalein': 'mh',
  'Pacific/Majuro': 'mh',
  'Pacific/Marquesas': 'pf',
  'Pacific/Midway': 'um',
  'Pacific/Nauru': 'nr',
  'Pacific/Niue': 'nu',
  'Pacific/Norfolk': 'nf',
  'Pacific/Noumea': 'nc',
  'Pacific/Pago_Pago': 'as',
  'Pacific/Palau': 'pw',
  'Pacific/Pitcairn': 'pn',
  'Pacific/Pohnpei': 'fm',
  'Pacific/Port_Moresby': 'pg',
  'Pacific/Rarotonga': 'ck',
  'Pacific/Saipan': 'mp',
  'Pacific/Tahiti': 'pf',
  'Pacific/Tarawa': 'ki',
  'Pacific/Tongatapu': 'to',
  'Pacific/Wake': 'um',
  'Pacific/Wallis': 'wf',
};
